export type RequestChannel = "fax" | "phone" | "epa" | "other";
export type RequesterRole =
  | "authorized_representative"
  | "enrollee"
  | "prescriber"
  | "pharmacist"
  | "member"
  | "servicing_provider"
  | "other";
export type SupportingStatementMethod = "fax" | "phone" | "electronic";

export const CASE_STATUS = [
  "open",
  "reopen",
  "closed",
  "complete",
  "amend",
  "request",
  "draft",
  "epa_pre_review",
  "proactive_outreach",
] as const;

export type CaseStatus = (typeof CASE_STATUS)[number];

export enum TriageQueue {
  Triage = "case_triage",
  NonDelegatedPrep = "non_delegated_prep",
}
export enum OutreachQueue {
  AppealsOutreach = "appeals_outreach",
  Outreach = "outreach",
  PreDecisionOutreach = "pre_decision_outreach",
  EroPreDecisionHold = "ero_pre_decision_hold",
  ProactiveOutreach = "proactive_outreach",
}
export enum ReviewQueue {
  Appeals = "appeals",
  AppealsAwaitingEroResponse = "appeals_awaiting_ero_response",
  AppealsInternalMDReview = "appeals_internal_md_review",
  AppealsEROReview = "appeals_ero_review",
  AwaitingEroResponse = "awaiting_ero_response",
  DMR = "dmr",
  ManagerReview = "manager_review",
  InternalMDReview = "internal_md_review",
  EroResponseReceived = "ero_response_received",
  EroReview = "ero_review",
  NonDelegatedReview = "non_delegated_review",
  Oncology = "oncology",
  RphReview = "rph_review",
  RxConcierge = "rx_concierge",
  TechReview = "tech_review",
}

export enum PostDeterminationQueue {
  PendingLetter = "pending_letter",
  VerbalNotification = "verbal_notification",
  PostReviewDiscussion = "post_review_discussion",
  AlternateNotification = "alternate_notification",
}

export enum OtherQueue {
  Hold = "hold",
  Other = "other",
}

export type CaseQueue = TriageQueue | OutreachQueue | ReviewQueue | OtherQueue | PostDeterminationQueue;

export const FAX_QUEUES = ["fax_triage", "fax_intake"] as const;

export type FaxQueue = (typeof FAX_QUEUES)[number];

export type AllQueueType = FaxQueue | CaseQueue;

export type NonDeterminationQueue = TriageQueue | OutreachQueue | OtherQueue;

export const NON_APPEAL_REVIEW_LEVELS = ["initial", "reconsideration", "renewal"];

export type ReviewLevel =
  | "initial"
  | "reconsideration"
  | "appeal_1"
  | "appeal_2"
  | "external_appeal"
  | "iro_preliminary_review"
  | "medicare_appeal_ire"
  | "medicare_appeal_alj"
  | "medicare_appeal_mac"
  | "medicare_appeal_jr"
  | "renewal";

export const CASE_ACTIONS = [
  "cancel_ero_review",
  "cancel_medical_director_review",
  "complete_deadline",
  "conclude_post_review_discussion",
  "determine_manager_review",
  "edit_determiner",
  "post_review_discussion",
  "reopen_case",
  "regulatory_reopen_case",
  "request_manager_review",
  "send_determination",
  "send_to_ero_review",
  "send_to_medical_director",
  "send_to_outreach",
  "send_to_triage",
  "start_reconsideration",
  "start_appeal_1",
  "start_appeal_2",
  "start_external_appeal",
  "start_iro_preliminary_review",
  "start_medicare_appeal_ire",
  "start_medicare_appeal_alj",
  "start_medicare_appeal_mac",
  "start_medicare_appeal_jr",
  "start_renewal",
  "close_case",
  "edit_case",
  "amend_case",
  "approve_case",
  "deny_case_administrative",
  "deny_case_clinical",
  "attach_fax_to_case",
  "get_next_v2",
] as const;

export type CaseActions = (typeof CASE_ACTIONS)[number];

export type CasePermittedAction = {
  readonly action: CaseActions;
  readonly metadata: {
    readonly external_case_record?: boolean;
  };
};

export type AvailableReviewLevel = {
  readonly review_level: ReviewLevel;
  readonly metadata: CasePermittedAction["metadata"];
};

export type PermittedActionResponseType = ReadonlyArray<CaseActions | CasePermittedAction>;

export const PHARMACY_TYPES = ["retail", "mail", "specialty"] as const;
export const SELECTABLE_PHARMACY_TYPES = [...PHARMACY_TYPES, "specific_npi"] as const;

export const DEFAULT_CUSTOMER_CARE_PHONE = "888-832-2779";

export type PharmacyType = (typeof PHARMACY_TYPES)[number];
export type SelectablePharmacyTypes = (typeof SELECTABLE_PHARMACY_TYPES)[number];

export type MultiSource = "M" | "O" | "N" | "Y";
export type Operators = "=" | "CONTAINS" | "STARTSWITH";

export interface CaseIdentityAttributes {
  first_name?: string;
  last_name?: string;
  job_title?: string;
}

export interface LockedBy {
  attributes?: CaseIdentityAttributes;
  identity?: string;
  warnings?: ReadonlyArray<string>;
  rn?: string;
}
export type CaseAuditEventAction = "INSERT" | "UPDATE" | "DELETE";

export enum CaseTags {
  ai_system_verbal_notification_eligible = "ai_system_verbal_notification_eligible",
  fragile_child = "fragile_child",
  gene_therapy = "gene_therapy",
  is_awaiting_prescriber_response = "is_awaiting_prescriber_response",
  is_awaiting_triage = "is_awaiting_triage",
  is_medical_director_review_required = "is_medical_director_review_required",
  is_awaiting_medical_director_review = "is_awaiting_medical_director_review",
  manager_review_required = "manager_review_required",
  is_medical_director_review_required_for_approval = "is_medical_director_review_required_for_approval",
  is_medical_director_specialty_match_required = "is_medical_director_specialty_match_required",
  is_medical_director_state_match_required = "is_medical_director_state_match_required",
  is_high_cost = "is_high_cost",
  is_high_touch_pa = "is_high_touch_pa",
  is_related_to_direct_member_reimbursement = "is_related_to_direct_member_reimbursement",
  is_medical_director_review_external = "is_medical_director_review_external",
  is_awaiting_external_review = "is_awaiting_external_review",
  is_eligible_for_tech_review = "is_eligible_for_tech_review",
  is_texas_department_of_insurance_case = "is_texas_department_of_insurance_case",
  approval_reviewed = "approval_reviewed",
  external_delegation = "external_delegation",
  history_section_reviewed = "history_section_reviewed"
}

export enum UrgencyUpgradeReason {
  corrected = "corrected",
  upgraded = "upgraded",
  created = "created",
}

export const SERVICE_STATUS_CHANGE_REASON = ["correction", "new_information"] as const;

export type ServiceStatusChangeReason = (typeof SERVICE_STATUS_CHANGE_REASON)[number];

// lock_mode

export enum CaseLockMode {
  Amend = "amend",
  ReOpen = "reopen",
  Edit = "edit",
}

export const LOCK_REASONS = [
  "adjust_date_and_duration",
  "adjust_communication",
  "adjust_dispensing_site",
  "adjust_facility_or_prescriber",
  "adjust_md_or_specialty",
  "alternate_notification_format",
  "manager_review_decision",
  "update_review_type",
  "update_benefit_type",
  "update_denial_type",
  "update_diagnosis",
  "update_drug_quantity_or_days_supply",
  "update_service_status",
] as const;

export type CaseLockReasons = (typeof LOCK_REASONS)[number];

export const DETERMINATION_TYPE = [
  "prior_authorization_coverage_determination",
  "step_therapy_coverage_determination",
  "safety_edit_coverage_determination",
  "benefit_exclusion",
] as const;

export const DMR_REVIEW_TYPES = ["payment"] as const;

export const NON_MEDICARE_REVIEW_TYPES = ["quantity_limit", "hospice", "aca_drug", "high_cost_pa", "high_dollar_pa"] as const;

export const EXEMPTION_TYPE = [
  "case_management_exception",
  "tiering_exception",
  "non_formulary_exception",
  "hospice_exception",
  "daw_penalty_exception",
] as const;

export const FORMULARY_UM_EXCEPTION_TYPE = [
  "prior_authorization_exception",
  "step_therapy_exception",
  "quantity_limit_exception",
  "safety_edit_exception",
  "cumulative_opioid_dose_safety_exception",
  "duplicate_long_acting_opioid_safety_exception",
  "opioid_benzodiazepine_safety_exception",
  "opioid_naive_safety_exception",
] as const;

export const NO_REVIEW_NEEDED_REVIEW_TYPE = ["no_clinical_review_needed", "no_review_needed"] as const;

export const BENEFIT_REVIEW_TYPE = ["part_b_vs_d", "part_d_eligibility"] as const;

export const OUTREACH_REVIEW_TYPE = ["proactive_outreach"] as const;

export const ALL_REVIEW_TYPES = [
  ...DETERMINATION_TYPE,
  ...DMR_REVIEW_TYPES,
  ...NON_MEDICARE_REVIEW_TYPES,
  ...EXEMPTION_TYPE,
  ...FORMULARY_UM_EXCEPTION_TYPE,
  ...NO_REVIEW_NEEDED_REVIEW_TYPE,
  ...BENEFIT_REVIEW_TYPE,
  ...OUTREACH_REVIEW_TYPE,
] as const;

export type DeterminationType = (typeof DETERMINATION_TYPE)[number];
export type ExceptionType = (typeof EXEMPTION_TYPE)[number];
export type FormularyUMExceptionType = (typeof FORMULARY_UM_EXCEPTION_TYPE)[number];

export type ReviewType = (typeof ALL_REVIEW_TYPES)[number];

export type ReviewTypeList = ReadonlyArray<ReviewType> | Record<string, ReadonlyArray<ReviewType>>;

export const DISPENSING_SITE_TYPE = ["pharmacy", "prescriber_administered"] as const;

export const DISPENSING_SITE_NAMES = {
  pharmacy: "Pharmacy",
  prescriber_administered: "Prescriber Administered",
} as const;

export type DispensingSite = (typeof DISPENSING_SITE_TYPE)[number];

export type BenefitType = "part_d" | "part_b_drugs" | "part_b_service_item";

export const DENIAL_TYPES = ["medical_necessity", "missing_information", "excluded_benefit"] as const;

export const DENIAL_TYPES_NAMES = {
  medical_necessity: "Medical Necessity",
  missing_information: "Missing Information",
  excluded_benefit: "Excluded Benefit",
} as const;

export type DenialTypes = (typeof DENIAL_TYPES)[number];

export const CLINICAL_DENIAL_TYPES: DenialTypes[] = ["medical_necessity", "missing_information"];
export const ADMINISTRATIVE_DENIAL_TYPES: DenialTypes[] = ["excluded_benefit"];

export const PERMISSIONS_FOR_DENIAL_TYPE: Partial<Record<CaseActions, DenialTypes[]>> = {
  deny_case_administrative: ADMINISTRATIVE_DENIAL_TYPES,
  deny_case_clinical: CLINICAL_DENIAL_TYPES,
};

export enum CoverageStrategyTypes {
  Commercial = "commercial",
  Medicare = "medicare",
  Medicaid = "medicaid",
  Exchange = "exchange",
}

export const SERVICE_STATUS = ["concurrent", "post_service", "pre_service"] as const;

export type ServiceStatus = (typeof SERVICE_STATUS)[number];

export const COPY_CASE_REASONS = {
  additional_review_type: "Additional Review Type",
  brand_vs_generic: "Brand vs Generic",
  rebuild_case: "Rebuild Case",
  other: "Other (specify in Note)",
} as const;

export type CopyCaseReason = keyof typeof COPY_CASE_REASONS;

export type ReopenedReason = "modify_decision" | "peer_to_peer" | "post_review_discussion" | "regulatory_reopen";

export type TestClaimOriginator =
  | "pa:auto_approve"
  | "pa:auto_decision"
  | "pa:determination"
  | "pa:determination_amend"
  | "pa:determination_reopen"
  | "pa:epa"
  | "pa:intake"
  | "pa:one_click"
  | "pa:system_approve"
  | "pa:test_claim_runner"
  | "pa:test_claim_tab";

export type ProductType = "custom" | "standard_drug" | "unavailable";

export interface CaseAttributes {
  dose_rounding_applied?: boolean;
}
